import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  type AddResourcePlacement,
  type SignalListRow,
  useAddResourceFromSignal,
} from "@/hooks/use-signals";

type PrimaryRole = "R1" | "R2" | "R3" | "R4";

/** One Primary Position row from /api/dashboard/solutions/:id/pod-positions. */
interface PodPosition {
  id: string | null;
  positionCode: PrimaryRole;
  filledBy: { resourceId: string; resourceName: string } | null;
}

const SUPPORTING_VALUE = "supporting";

async function fetchPositions(solutionId: string): Promise<PodPosition[]> {
  const res = await fetch(`/api/dashboard/solutions/${solutionId}/pod-positions`);
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error ?? `Request failed: ${res.status}`);
  }
  return (await res.json()) as PodPosition[];
}

/**
 * Placement chooser for a `resource_off_pod` signal: pick an unfilled Primary
 * Position (R1–R4) or Supporting, then add the off-Pod Resource there via the
 * normal audited staffing path. Filled Primary Positions are shown disabled (who
 * holds them) so the RM never silently replaces a Primary from the queue.
 */
export function AddResourceDialog({
  signal,
  open,
  onOpenChange,
}: {
  signal: SignalListRow;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [choice, setChoice] = useState<string>(SUPPORTING_VALUE);
  const addResource = useAddResourceFromSignal();

  const positionsQuery = useQuery({
    queryKey: ["solution-pod-positions", signal.solutionId],
    queryFn: () => fetchPositions(signal.solutionId),
    enabled: open,
  });

  const positions = useMemo(() => positionsQuery.data ?? [], [positionsQuery.data]);
  const resourceName = signal.suggestedResourceName ?? "this Resource";

  function submit() {
    const placement: AddResourcePlacement =
      choice === SUPPORTING_VALUE ? { supporting: true } : { role: choice as PrimaryRole };
    addResource.mutate(
      { signalId: signal.id, solutionId: signal.solutionId, placement },
      { onSuccess: () => onOpenChange(false) },
    );
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        onOpenChange(next);
        if (next) setChoice(SUPPORTING_VALUE);
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add {resourceName} to the Pod</DialogTitle>
          <DialogDescription>
            {resourceName} has demand on {signal.solutionName} but no assignment. Choose where to
            place them — a Primary Position or Supporting. This staffs them and recalculates the
            Solution's Fit Score.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-1.5">
          <Label htmlFor="add-resource-placement">Placement</Label>
          <Select value={choice} onValueChange={setChoice}>
            <SelectTrigger id="add-resource-placement">
              <SelectValue placeholder="Choose a placement" />
            </SelectTrigger>
            <SelectContent>
              {(["R1", "R2", "R3", "R4"] as const).map((role) => {
                const pos = positions.find((p) => p.positionCode === role);
                const filled = pos?.filledBy ?? null;
                return (
                  <SelectItem key={role} value={role} disabled={filled !== null}>
                    {role} (Primary)
                    {filled ? ` — filled by ${filled.resourceName}` : ""}
                  </SelectItem>
                );
              })}
              <SelectItem value={SUPPORTING_VALUE}>Supporting</SelectItem>
            </SelectContent>
          </Select>
          {positionsQuery.isError && (
            <p className="text-xs text-destructive">
              Couldn't load Pod Positions — Supporting is still available.
            </p>
          )}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="button" disabled={addResource.isPending} onClick={submit}>
            Add to Pod
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
